import { motion, type PanInfo } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiDownload,
  FiFilter,
  FiSearch,
} from "react-icons/fi";
import { MdRestaurant } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { PuffLoader } from "react-spinners";
import type {
  MealCompletePickupData,
  MealCompletePickupPayload,
  MealMonitoringDetailData,
  MealMonitoringDetailItem,
  MealMonitoringDetailStatus,
  MealMonitoringDetailTab,
} from "../../../api/meal/types/mealMonitoringDetailType";
import Colors from "../../../helpers/Colors";
import MealDetailFilterPopup from "./component/detail/MealDetailFilterPopup";
import MealDetailItem from "./component/detail/MealDetailItem";
import MealDetailSummary from "./component/detail/MealDetailSummary";
import MealDetailTabBar from "./component/detail/MealDetailTabBar";
import MealOutOfStockPopup from "./component/detail/MealOutOfStockPopup";
import MealPickupSuccessPopup from "./component/detail/MealPickupSuccessPopup";
import MealMonitoringDownloadModal from "./component/monitoring/MealMonitoringDownloadModal";
import { useMealMonitoringDetail } from "./hooks/useMealMonitoringDetail";
import {
  applyLocalMealCompletion,
  buildCompletedMealItem,
} from "./utils/mealMonitoringDetailLocal";

interface MealMonitoringDetailLocationState {
  mealUuid?: string;
  activeTab?: string;
  positionName?: string;
}

const swipeVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 70 : -70,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
};

const UNALLOCATED_TAB_TITLE = "Belum Dialokasikan";

const isOutOfStockError = (message: string): boolean => {
  const normalized = message.trim().toLowerCase();

  return (
    normalized.includes("stok tidak tersedia") ||
    normalized.includes("stok habis") ||
    normalized.includes("out of stock")
  );
};

const MealMonitoringDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigationState =
    location.state as MealMonitoringDetailLocationState | null;

  const mealUuid = navigationState?.mealUuid;
  const positionName = navigationState?.positionName?.trim().toUpperCase();
  const isSustainability = positionName === "SUSTAINABILITY";
  const isCleaningService = positionName === "P-CLEANING SERVICE";
  const isAllowedPosition = isSustainability || isCleaningService;

  const {
    detail,
    isLoadingDetail,
    detailError,
    getDetail,
    isCompletingPickup,
    completePickupError,
    completePickupMeal,
    clearPickup,
    clearPickupError,
    reset,
    downloadForm,
    isDownloading,
    downloadError,
  } = useMealMonitoringDetail();

  const [localDetail, setLocalDetail] =
    useState<MealMonitoringDetailData | null>(null);

  const currentDetail =
    localDetail?.meal.uuid === mealUuid ? localDetail : null;

  const [activeTab, setActiveTab] = useState(
    navigationState?.activeTab ?? "Pesanan",
  );
  const [direction, setDirection] = useState(1);
  const [completingItemKey, setCompletingItemKey] = useState<string | null>(
    null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<
    MealMonitoringDetailStatus[]
  >([]);
  const [appliedDepartments, setAppliedDepartments] = useState<number[]>([]);
  const [appliedStatuses, setAppliedStatuses] = useState<
    MealMonitoringDetailStatus[]
  >([]);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [pickupSuccessItem, setPickupSuccessItem] =
    useState<MealMonitoringDetailItem | null>(null);
  const [pickupSuccessData, setPickupSuccessData] =
    useState<MealCompletePickupData | null>(null);
  const [isPickupSuccessOpen, setIsPickupSuccessOpen] = useState(false);
  const [isOutOfStockOpen, setIsOutOfStockOpen] = useState(false);
  const [outOfStockMessage, setOutOfStockMessage] = useState<string | null>(
    null,
  );
  const completeControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      completeControllerRef.current?.abort();
      completeControllerRef.current = null;
      reset();
    };
  }, [reset]);

  useEffect(() => {
    if (!isAllowedPosition || !mealUuid) {
      navigate("/", {
        replace: true,
      });
    }
  }, [isAllowedPosition, mealUuid, navigate]);

  useEffect(() => {
    if (detail?.meal.uuid === mealUuid) {
      setLocalDetail(detail);
    }
  }, [detail, mealUuid]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (!mealUuid || !isAllowedPosition) {
      return;
    }

    const request = getDetail(mealUuid, {
      search: search || undefined,
      department:
        appliedDepartments.length > 0 ? appliedDepartments : undefined,
      status: appliedStatuses.length > 0 ? appliedStatuses : undefined,
    });

    return () => {
      request.abort();
    };
  }, [
    mealUuid,
    isAllowedPosition,
    search,
    appliedDepartments,
    appliedStatuses,
    getDetail,
  ]);

  const tabs = useMemo<MealMonitoringDetailTab[]>(
    () => currentDetail?.meal.tab ?? [],
    [currentDetail],
  );

  const tabTitles = useMemo(() => tabs.map((tab) => tab.title), [tabs]);

  const activeTabData = useMemo(() => {
    return tabs.find((tab) => tab.title === activeTab) ?? tabs[0];
  }, [tabs, activeTab]);

  useEffect(() => {
    if (tabs.length === 0) {
      return;
    }

    const activeExists = tabs.some((tab) => tab.title === activeTab);

    if (!activeExists) {
      setActiveTab(tabs[0].title);
      setDirection(1);
    }
  }, [tabs, activeTab]);

  const getItemKey = useCallback((item: MealMonitoringDetailItem) => {
    return (
      item.uuid ??
      `${item.employee_id ?? "external"}-${item.nrp}-${item.menu_id ?? "none"}-${item.type}`
    );
  }, []);

  const isExternalItem = useCallback((item: MealMonitoringDetailItem) => {
    return (
      item.employee_id === null || item.type.trim().toLowerCase() === "external"
    );
  }, []);

  const isSearchPending = searchInput.trim() !== search;

  const canCompleteItem = useCallback(
    (item: MealMonitoringDetailItem) => {
      if (
        !isCleaningService ||
        isCompletingPickup ||
        isLoadingDetail ||
        isSearchPending
      ) {
        return false;
      }

      if (localDetail?.meal?.status?.trim().toLowerCase() !== "order") {
        return false;
      }

      const status = item.status?.trim().toLowerCase();

      if (status !== "ordered") {
        return false;
      }

      if (isExternalItem(item)) {
        return item.uuid !== null && item.uuid.trim() !== "";
      }

      return item.employee_id !== null && Boolean(mealUuid);
    },
    [
      isCleaningService,
      isCompletingPickup,
      isLoadingDetail,
      isSearchPending,
      isExternalItem,
      localDetail?.meal?.status,
      mealUuid,
    ],
  );

  const handleTabChange = useCallback(
    (nextTab: string) => {
      if (nextTab === activeTab) {
        return;
      }

      const currentIndex = tabTitles.indexOf(activeTab);
      const nextIndex = tabTitles.indexOf(nextTab);

      if (currentIndex === -1 || nextIndex === -1) {
        return;
      }

      setDirection(nextIndex > currentIndex ? 1 : -1);
      setActiveTab(nextTab);
    },
    [activeTab, tabTitles],
  );

  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (tabTitles.length <= 1) {
        return;
      }

      const currentIndex = tabTitles.indexOf(activeTab);

      if (currentIndex === -1) {
        return;
      }

      const offsetX = info.offset.x;
      const velocityX = info.velocity.x;
      const isSwipeLeft = offsetX < -50 || velocityX < -400;
      const isSwipeRight = offsetX > 50 || velocityX > 400;

      if (isSwipeLeft) {
        const nextIndex = currentIndex + 1;

        if (nextIndex < tabTitles.length) {
          handleTabChange(tabTitles[nextIndex]);
        }

        return;
      }

      if (isSwipeRight) {
        const nextIndex = currentIndex - 1;

        if (nextIndex >= 0) {
          handleTabChange(tabTitles[nextIndex]);
        }
      }
    },
    [activeTab, handleTabChange, tabTitles],
  );

  const handleApplyFilter = () => {
    setAppliedDepartments(selectedDepartments);
    setAppliedStatuses(selectedStatuses);
    setIsFilterOpen(false);
  };

  const handleResetFilter = () => {
    setSelectedDepartments([]);
    setSelectedStatuses([]);
    setAppliedDepartments([]);
    setAppliedStatuses([]);
    setIsFilterOpen(false);
  };

  const handleComplete = async (item: MealMonitoringDetailItem) => {
    if (completingItemKey || !canCompleteItem(item) || !currentDetail) {
      return;
    }

    const itemKey = getItemKey(item);
    const sourceTabTitle = activeTabData?.title ?? activeTab;
    const isUnallocatedTab = sourceTabTitle === UNALLOCATED_TAB_TITLE;
    let payload: MealCompletePickupPayload;

    if (isExternalItem(item)) {
      if (!item.uuid) {
        return;
      }

      payload = {
        uuid_items: item.uuid,
      };
    } else {
      if (item.employee_id === null || !mealUuid) {
        return;
      }

      payload = {
        employee_id: item.employee_id,
        uuid_meals: mealUuid,
      };
    }

    completeControllerRef.current?.abort();
    const controller = new AbortController();
    completeControllerRef.current = controller;

    try {
      setCompletingItemKey(itemKey);
      clearPickupError();

      const pickupData = await completePickupMeal(payload, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      const completedItem = buildCompletedMealItem(
        currentDetail,
        item,
        pickupData,
      );

      setLocalDetail((previousDetail) => {
        if (!previousDetail || previousDetail.meal.uuid !== mealUuid) {
          return previousDetail;
        }

        return applyLocalMealCompletion({
          detail: previousDetail,
          sourceTabTitle,
          item,
          pickupData,
          statusFilter: appliedStatuses,
        }).detail;
      });

      if (isUnallocatedTab) {
        setPickupSuccessItem(completedItem);
        setPickupSuccessData(pickupData);
        setIsPickupSuccessOpen(true);
      } else {
        toast.success("Meal berhasil diambil");
      }

      setCompletingItemKey(null);
    } catch (error) {
      if (controller.signal.aborted) {
        return;
      }

      const errorMessage =
        error && typeof error === "object" && "message" in error
          ? String((error as { message?: unknown }).message ?? "")
          : "";

      if (isOutOfStockError(errorMessage)) {
        clearPickupError();
        setOutOfStockMessage(errorMessage);
        setIsOutOfStockOpen(true);
        return;
      }

      setCompletingItemKey(null);
    } finally {
      if (completeControllerRef.current === controller) {
        completeControllerRef.current = null;
      }
    }
  };

  const handlePickupSuccessConfirm = () => {
    setIsPickupSuccessOpen(false);
    setPickupSuccessItem(null);
    setPickupSuccessData(null);
    clearPickup();
  };

  const handleOutOfStockClose = () => {
    setIsOutOfStockOpen(false);
    setOutOfStockMessage(null);
    setCompletingItemKey(null);
    clearPickupError();
  };

  const handleDownload = async (): Promise<boolean> => {
    if (!mealUuid || isDownloading) {
      return false;
    }

    const success = await downloadForm(
      mealUuid,
      `${currentDetail?.meal.meal_type ?? "meal"}-detail`,
    );

    if (success) {
      setShowDownloadModal(false);
    }

    return success;
  };

  if (!isAllowedPosition || !mealUuid) {
    return null;
  }

  if (!currentDetail) {
    if (isLoadingDetail) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <PuffLoader color={Colors.primary} size={42} />
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <p className="text-sm font-semibold text-gray-700">
          Gagal memuat detail meal
        </p>

        <p className="mt-1 text-xs text-gray-400">
          {detailError ?? "Data tidak tersedia"}
        </p>

        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mt-4 rounded-lg bg-[#ff706b] px-5 py-2 text-xs font-semibold text-white"
        >
          Kembali
        </button>
      </div>
    );
  }

  const items = activeTabData?.items ?? [];
  const summary = activeTabData?.summary ?? null;
  const isRefetching = isLoadingDetail;
  const isFilterActive =
    appliedDepartments.length > 0 || appliedStatuses.length > 0;
  const actionError = completePickupError ?? downloadError;

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <header className="sticky top-0 z-40 flex h-14 items-center bg-white px-4 shadow-sm">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center"
        >
          <FiArrowLeft className="text-[22px] text-[#444]" />
        </button>

        <h1 className="flex-1 truncate text-center text-[16px] font-bold text-[#444]">
          Detail {currentDetail.meal.meal_type}
        </h1>

        <button
          type="button"
          onClick={() => !isDownloading && setShowDownloadModal(true)}
          disabled={isDownloading}
          className="flex h-9 w-9 items-center justify-center disabled:opacity-50"
        >
          {isDownloading ? (
            <PuffLoader color="#444" size={16} />
          ) : (
            <FiDownload className="text-[20px] text-[#444]" />
          )}
        </button>
      </header>

      <motion.main
        className="min-h-[calc(100dvh-56px)] px-4 pb-6 pt-3"
        drag="x"
        dragDirectionLock
        dragMomentum={false}
        dragElastic={0.08}
        dragConstraints={{
          left: 0,
          right: 0,
        }}
        onDragEnd={handleDragEnd}
        style={{
          touchAction: "pan-y",
        }}
      >
        {(detailError || actionError) && (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5 text-[10px] leading-relaxed text-red-500">
            <FiAlertCircle className="mt-0.5 shrink-0 text-[14px]" />
            <span>{actionError ?? detailError}</span>
          </div>
        )}

        <div className="flex gap-2">
          <div className="flex h-11 flex-1 items-center rounded-xl bg-white px-4 shadow-sm">
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              disabled={isCompletingPickup}
              placeholder="Type Name/NRP"
              className="min-w-0 flex-1 bg-transparent text-[12px] text-[#555] outline-none placeholder:text-[#b4b4c4] disabled:opacity-60"
            />

            {isRefetching && searchInput ? (
              <PuffLoader color="#a7aac0" size={14} />
            ) : (
              <FiSearch className="shrink-0 text-[19px] text-[#a7aac0]" />
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              if (isCompletingPickup) {
                return;
              }

              setSelectedDepartments(appliedDepartments);
              setSelectedStatuses(appliedStatuses);
              setIsFilterOpen(true);
            }}
            disabled={isCompletingPickup}
            className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm disabled:opacity-60 ${
              isFilterActive ? "text-[#ff706b]" : "text-[#a7aac0]"
            }`}
          >
            <FiFilter className="text-[18px]" />

            {isFilterActive && (
              <span className="absolute right-2 top-2 h-1.5 w-1.5 rounded-full bg-[#ff706b]" />
            )}
          </button>
        </div>

        <div className="mt-3">
          <MealDetailTabBar
            tabs={tabs}
            activeTab={activeTab}
            onChange={handleTabChange}
          />
        </div>

        <div className="relative mt-3">
          {isRefetching && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center rounded-xl bg-white/60 pt-10">
              <PuffLoader color={Colors.primary} size={32} />
            </div>
          )}

          <motion.div
            key={activeTab}
            custom={direction}
            variants={swipeVariants}
            initial="enter"
            animate="center"
            transition={{
              duration: 0.22,
              ease: "easeOut",
            }}
            className="w-full"
          >
            {summary && (
              <div className="mb-3">
                <MealDetailSummary summary={summary} />
              </div>
            )}

            {summary && (
              <div className="mb-3">
                <p className="text-[13px] text-[#999]">
                  <span className="font-bold text-[#ff706b]">
                    {summary.taken}
                  </span>
                  <span>/{summary.total} Meals taken</span>
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl bg-white px-6 py-11 text-center shadow-sm">
                  <div className="relative mb-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#fff3f2]">
                      <MdRestaurant className="text-[30px] text-[#ff7772]" />
                    </div>

                    <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-[#ffe3e1] text-[11px]">
                      🍽️
                    </span>
                  </div>

                  <p className="text-sm font-bold text-[#444]">
                    Tidak ada data
                  </p>

                  <p className="mt-1 max-w-[230px] text-[11px] leading-relaxed text-gray-400">
                    Belum ada meal yang sesuai dengan kategori atau filter yang
                    dipilih.
                  </p>
                </div>
              ) : (
                items.map((item) => {
                  const itemKey = getItemKey(item);

                  return (
                    <MealDetailItem
                      key={itemKey}
                      item={item}
                      activeTab={activeTabData?.title ?? activeTab}
                      isCleaningService={isCleaningService}
                      canComplete={canCompleteItem(item)}
                      isCompleting={completingItemKey === itemKey}
                      onComplete={handleComplete}
                    />
                  );
                })
              )}
            </div>
          </motion.div>
        </div>
      </motion.main>

      <MealDetailFilterPopup
        open={isFilterOpen}
        departments={currentDetail.filter.department}
        statuses={currentDetail.filter.status}
        selectedDepartments={selectedDepartments}
        selectedStatuses={selectedStatuses}
        onDepartmentChange={setSelectedDepartments}
        onStatusChange={setSelectedStatuses}
        onReset={handleResetFilter}
        onApply={handleApplyFilter}
        onClose={() => setIsFilterOpen(false)}
      />

      <MealMonitoringDownloadModal
        open={showDownloadModal}
        loading={isDownloading}
        onClose={() => !isDownloading && setShowDownloadModal(false)}
        onDownload={handleDownload}
      />

      <MealPickupSuccessPopup
        open={isPickupSuccessOpen}
        item={pickupSuccessItem}
        pickupData={pickupSuccessData}
        onConfirm={handlePickupSuccessConfirm}
      />

      <MealOutOfStockPopup
        open={isOutOfStockOpen}
        message={outOfStockMessage}
        onClose={handleOutOfStockClose}
      />
    </div>
  );
};

export default MealMonitoringDetailPage;
